<template>
  <slot
    name="any"
    :data="data"
    :refresh="launchGettingFlow"
    :loading="loading"
    :loadingError="loadingError"
  >
    <div>
      <div v-if="loadingError">
        <slot :refresh="launchGettingFlow" name="error">
          <NoDataView :refresh="launchGettingFlow"></NoDataView>
        </slot>
      </div>

      <div v-else>
        <slot :data="data" :refresh="launchGettingFlow" :loading="loading">
          {{ loading ? 'Loading...' : '' }}
          <div v-html="Commons.jsonToHtml(data)"></div>
        </slot>
      </div>

      <!-- DataFetcher instances invisibles -->
      <div style="display: none">
        <DataFetcher
          v-for="(key, idx) in dataKeys"
          :key="idx"
          :dataKey="key"
          :filters="filters[idx] || {}"
          :refreshDelay="refreshDelay"
          :refreshOnEvents="refreshOnEvents"
          :debounceDelay="debounceDelay"
          :log="log"
          @fetched="onChildFetched(idx, $event.data)"
          @beforeGetting="onChildBeforeGetting(idx)"
          @startGetting="onChildStartGetting(idx)"
          @loadingError="onChildLoadingError(idx)"
          ref="childFetchers"
        />
      </div>
    </div>
  </slot>
</template>

<script>
import { defineComponent, ref } from 'vue'
import DataFetcher from './DataFetcher.vue'
import NoDataView from './NoDataView.vue'
import Commons from '@/shared/utils/commons'

export default defineComponent({
  name: 'MultiDataFetcher',
  components: { DataFetcher, NoDataView },
  emits: ['fetched', 'beforeGetting', 'startGetting', 'loadingError'],
  props: {
    dataKeys: { type: Array, required: true },
    filters: { type: Array, default: () => [] },
    refreshDelay: { type: Number },
    refreshOnEvents: { type: [String, Array] },
    debounceDelay: { type: Number },
    log: { type: Boolean, default: false },
  },
  setup() {
    const data = ref([])
    const loading = ref(true)
    const loadingError = ref(false)
    const childStates = ref({})

    return { data, loading, loadingError, childStates, Commons }
  },
  methods: {
    launchGettingFlow() {
      this.loading = true
      this.loadingError = false
      this.childStates = {}
      const children = this.$refs.childFetchers || []
      children.forEach(child => {
        if (child && child.launchGettingFlow) child.launchGettingFlow()
      })
    },

    onChildBeforeGetting(key) {
      this.childStates[key] = {
        ...this.childStates[key],
        loading: true,
        error: false,
      }
      this.aggregateDataAndEmit()
      this.$emit('beforeGetting', { key }) // emission globale
    },

    onChildStartGetting(key) {
      if (this.log) Commons.log('MultiDataFetcher: startGetting', key)
      this.aggregateDataAndEmit()
      this.$emit('startGetting', { key }) // emission globale
    },

    onChildFetched(key, childData) {
      this.childStates[key] = { error: false, loading: false, data: childData }
      this.aggregateDataAndEmit()
    },

    onChildLoadingError(key) {
      this.childStates[key] = { error: true, loading: false, data: null }
      this.loadingError = true
      this.loading = false
      this.$emit('loadingError') // émis immédiatement, aucune donnée
    },

    aggregateDataAndEmit() {
      const aggregated = []
      let anyLoading = false
      let anyError = false

      this.dataKeys.forEach((_, index) => {
        const state = this.childStates[index] || {}
        if (state.loading) anyLoading = true
        if (state.error) anyError = true
        aggregated.push(state.data ?? null)
      })

      this.data = aggregated
      this.loading = anyLoading
      this.loadingError = anyError

      // n'émettre fetched que si tout est chargé et aucune erreur
      if (!anyLoading && !anyError) {
        this.$emit('fetched', { data: this.data }) // données complètes sans clé
      }
    },
  },
  mounted() {
    this.launchGettingFlow()
  },
})
</script>
